import { Provider } from "jotai";
import { Suspense, useEffect } from "react";
import { useQueryLoader } from "react-relay";

import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { convertDateObjToServerStringFormat } from "@whoosh/utilities/src/dates";

import { LessonBookingWizardQueryRenderer } from "./components/BookingWizardQueryRenderer";
import { LessonBookingWizardQueryRendererQueryString } from "./components/BookingWizardQueryRenderer/LessonBookingWizardQueryRenderer";
import {
  LessonBookingWizardQueryRendererQuery,
  SlotType,
} from "./components/BookingWizardQueryRenderer/__generated__/LessonBookingWizardQueryRendererQuery.graphql";

type LessonBookingWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  slotId: string | null;
  date: string | null;
  membershipTypeId: string | null;
  currentPatronId: string | null;
  clubSlug: string;
  slotType: SlotType;
  facilityId?: string;
  instructorId: string;
  lessonTypeId: string;
  canSelectVenue: boolean;
  slots: Array<{
    date: string;
    durationMins: number;
    id: string;
    time: string;
  }>;
  requestedTime?: string | null;
  durationMins?: number;
};

export const LessonBookingWizard = ({
  slots,
  date,
  isOpen,
  onClose,
  slotId,
  membershipTypeId,
  currentPatronId,
  clubSlug,
  slotType,
  facilityId,
  instructorId,
  lessonTypeId,
  canSelectVenue,
  requestedTime,
  durationMins,
}: LessonBookingWizardProps) => {
  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton={true}
      disableContentScroll={true}
      content={
        <LessonBookingWizardContent
          slots={slots}
          date={date}
          currentPatronId={currentPatronId}
          onClose={onClose}
          slotId={slotId}
          isOpen={isOpen}
          membershipTypeId={membershipTypeId}
          clubSlug={clubSlug}
          slotType={slotType}
          facilityId={facilityId}
          instructorId={instructorId}
          lessonTypeId={lessonTypeId}
          canSelectVenue={canSelectVenue}
          requestedTime={requestedTime}
          durationMins={durationMins}
        />
      }
    />
  );
};

type LessonBookingWizardContentProps = LessonBookingWizardProps;

const LessonBookingWizardContent = ({
  slots,
  slotId,
  isOpen,
  onClose,
  membershipTypeId,
  currentPatronId,
  date,
  clubSlug,
  slotType,
  facilityId,
  instructorId,
  lessonTypeId,
  canSelectVenue,
  requestedTime,
  durationMins,
}: LessonBookingWizardContentProps) => {
  // Lesson booking query loader
  const [queryRef, loadQuery] =
    useQueryLoader<LessonBookingWizardQueryRendererQuery>(
      LessonBookingWizardQueryRendererQueryString
    );

  const currentDate = date
    ? date
    : convertDateObjToServerStringFormat(new Date());

  useEffect(() => {
    if (isOpen && slotId && membershipTypeId) {
      // Load lesson booking query
      loadQuery(
        {
          slotId,
          membershipTypeId,
          players: [currentPatronId],
          currentDate,
          slotType,
          clubSlug,
          facilityId: facilityId || "",
          instructorId,
          lessonTypeId,
          bookingType: "LESSON",
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [
    clubSlug,
    currentDate,
    currentPatronId,
    facilityId,
    instructorId,
    isOpen,
    lessonTypeId,
    loadQuery,
    membershipTypeId,
    slotId,
    slotType,
  ]);

  return (
    <Provider>
      <Suspense fallback={null}>
        {queryRef ? (
          <LessonBookingWizardQueryRenderer
            slots={slots}
            queryRef={queryRef}
            onClose={onClose}
            lessonTypeId={lessonTypeId}
            canSelectVenue={canSelectVenue}
            requestedTime={requestedTime}
            durationMins={durationMins}
          />
        ) : null}
      </Suspense>
    </Provider>
  );
};
